import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { SparklesIcon, SearchIcon, SunIcon, HeartIcon, SaveIcon } from '../components/icons';

import { useSEO } from '../hooks/useSEO';

import { carouselItems } from '../data/carouselData';

const TravelPage: React.FC = () => {
    const { t } = useTranslation();
    const [destination, setDestination] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [packingList, setPackingList] = useState<string[] | null>(null);

    // Filter for Travel items
    const travelLookbooks = carouselItems.filter(item => item.category === 'Travel' || item.category === 'Lifestyle');

    useSEO(
        t('travelPage_seo_title'),
        t('travelPage_seo_desc'),
        t('travelPage_seo_keywords').split(',').map(k => k.trim())
    );

    const handleGenerate = () => {
        setIsGenerating(true);
        // Simulate AI generation
        setTimeout(() => {
            setPackingList([
                t('travelPage_item_blazer'),
                t('travelPage_item_camisole'),
                t('travelPage_item_trousers'),
                t('travelPage_item_sandals'),
                t('travelPage_item_earrings'),
                t('travelPage_item_hat'),
                t('travelPage_item_clutch'),
                t('travelPage_item_swimsuit')
            ]);
            setIsGenerating(false);
        }, 2000);
    };

    return (
        <div className="pt-24 pb-16 px-4 max-w-7xl mx-auto min-h-screen">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-16"
            >
                <div className="flex justify-center mb-6">
                    <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-full text-indigo-600 dark:text-indigo-400">
                        <SparklesIcon className="w-8 h-8" />
                    </div>
                </div>
                <h1 className="text-5xl mb-6 text-deep-charcoal dark:text-white font-serif">{t('travelPage_seo_title')}</h1>
                <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto font-sans">
                    {t('travelPage_seo_desc')}
                </p>
            </motion.div>

            {/* Travel Diaries Carousel */}
            <div className="mb-20">
                <div className="flex items-center justify-between mb-8">
                    <h2 className="text-3xl font-serif italic text-gray-800 dark:text-white">{t('travelPage_diaries_title')}</h2>
                    <div className="h-px flex-grow mx-8 bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent"></div>
                </div>

                <div className="relative group">
                    <div className="flex overflow-x-auto gap-6 pb-12 snap-x snap-mandatory hide-scrollbar">
                        {travelLookbooks.map((item, idx) => (
                            <motion.a
                                key={idx}
                                href={item.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                initial={{ opacity: 0, scale: 0.95 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ delay: idx * 0.1 }}
                                viewport={{ once: true }}
                                className="flex-shrink-0 w-80 snap-center group/card"
                            >
                                <div className="aspect-[3/4] overflow-hidden rounded-2xl shadow-xl dark:shadow-black/40 relative">
                                    <img
                                        src={item.imageUrl}
                                        alt={t(item.titleKey)}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover/card:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 group-hover/card:opacity-80 transition-opacity"></div>
                                    <div className="absolute bottom-0 left-0 p-6 text-white transform translate-y-2 group-hover/card:translate-y-0 transition-transform">
                                        <p className="text-[10px] font-bold uppercase tracking-wider mb-2 text-indigo-400">{item.category}</p>
                                        <h3 className="text-xl font-serif italic leading-tight">
                                            {t(item.titleKey)}
                                        </h3>
                                    </div>
                                </div>
                            </motion.a>
                        ))}
                        {travelLookbooks.length === 0 && (
                            <div className="w-full py-20 text-center text-gray-400 dark:text-gray-600 italic border-2 border-dashed border-gray-100 dark:border-gray-800 rounded-3xl">
                                {t('travelPage_no_lookbooks')}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="max-w-xl mx-auto bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-2xl border border-gray-100 dark:border-white/5">
                <div className="mb-8">
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-3 ml-1">{t('travelPage_destination_label')}</label>
                    <div className="relative group">
                        <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5 group-focus-within:text-indigo-500 transition-colors" />
                        <input
                            type="text"
                            value={destination}
                            onChange={(e) => setDestination(e.target.value)}
                            placeholder={t('travelPage_destination_placeholder')}
                            className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-white/10 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none text-lg dark:text-white transition-all"
                        />
                    </div>
                </div>

                <div className="mb-8 grid grid-cols-2 gap-4">
                    <button className="p-4 border border-gray-200 dark:border-white/10 rounded-2xl flex flex-col items-center hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all hover:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20">
                        <SunIcon className="w-6 h-6 text-orange-400 mb-2" />
                        <span className="text-sm font-medium dark:text-gray-200">{t('travelPage_style_summer')}</span>
                    </button>
                    <button className="p-4 border border-gray-200 dark:border-white/10 rounded-2xl flex flex-col items-center hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all hover:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20">
                        <div className="w-6 h-6 text-blue-400 mb-2 font-bold text-center">❄️</div>
                        <span className="text-sm font-medium dark:text-gray-200">{t('travelPage_style_winter')}</span>
                    </button>
                </div>

                <button
                    onClick={handleGenerate}
                    disabled={!destination || isGenerating}
                    className={`w-full py-4 rounded-2xl text-white font-bold tracking-widest uppercase shadow-xl transition-all relative overflow-hidden group ${!destination || isGenerating ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed opacity-50' : 'bg-indigo-600 hover:bg-indigo-700 hover:scale-[1.02] active:scale-95'}`}
                >
                    <span className="relative z-10">{isGenerating ? t('travelPage_generating') : t('travelPage_generate_btn')}</span>
                    {!isGenerating && destination && (
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-shine"></div>
                    )}
                </button>
            </div>

            <AnimatePresence>
                {packingList && (
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="max-w-3xl mx-auto mt-20"
                    >
                        <h2 className="text-4xl text-center mb-12 font-serif italic dark:text-white">{t('travelPage_result_title', { destination })}</h2>
                        <div className="bg-white dark:bg-gray-800/80 backdrop-blur-md p-10 rounded-3xl shadow-2xl border border-gray-100 dark:border-white/5 grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12 relative overflow-hidden">
                            <div className="absolute -top-12 -right-12 w-40 h-40 bg-indigo-500/5 rounded-full blur-3xl"></div>
                            <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-purple-500/5 rounded-full blur-3xl"></div>

                            {packingList.map((item, idx) => (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: idx * 0.1 }}
                                    className="flex items-center gap-4 group/item"
                                >
                                    <div className="w-2 h-2 bg-indigo-500 rounded-full group-hover/item:scale-150 transition-transform"></div>
                                    <span className="text-lg text-gray-800 dark:text-gray-200 font-serif group-hover/item:text-indigo-500 transition-colors">{item}</span>
                                </motion.div>
                            ))}
                        </div>
                        <div className="text-center mt-12 flex justify-center gap-6">
                            <button className="flex items-center gap-2 px-6 py-3 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-bold uppercase tracking-widest hover:bg-indigo-600 hover:text-white transition-all shadow-lg shadow-indigo-500/10">
                                <HeartIcon className="w-5 h-5" />
                                {t('travelPage_save_diary')}
                            </button>
                            <button className="flex items-center gap-2 px-6 py-3 rounded-full border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">
                                <SaveIcon className="w-5 h-5" />
                                {t('travelPage_export_pdf')}
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default TravelPage;
